export function isValidTimeZone(
  zone: string | null | undefined,
): zone is string {
  if (!zone) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

const partsFormatters = new Map<string, Intl.DateTimeFormat>();

/** Milliseconds to add to a UTC instant to get the wall clock in `zone`. */
function zoneOffset(instant: number, zone: string): number {
  let formatter = partsFormatters.get(zone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: zone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
    partsFormatters.set(zone, formatter);
  }
  const part = (type: string) =>
    Number(
      formatter!.formatToParts(instant).find((p) => p.type === type)?.value,
    );
  const wall = Date.UTC(
    part('year'),
    part('month') - 1,
    part('day'),
    part('hour'),
    part('minute'),
    part('second'),
  );
  return wall - Math.floor(instant / 1000) * 1000;
}

/**
 * The instant at which the wall clock in `zone` reads `date` `time`
 * ('YYYY-MM-DD', 'HH:MM'). A repeated time (DST ends) resolves to the first
 * occurrence; a skipped time (DST starts) to the instant just after the jump.
 */
export function zonedTimeToUtc(date: string, time: string, zone: string): Date {
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  const first = wall - zoneOffset(wall, zone);
  const second = wall - zoneOffset(first, zone);
  const exact = [first, second]
    .filter((instant) => instant + zoneOffset(instant, zone) === wall)
    .sort((a, b) => a - b);
  return new Date(exact.length > 0 ? exact[0] : Math.max(first, second));
}

/** The calendar date in `zone` at that instant. */
export function zonedDate(
  at: Date,
  zone: string,
): { year: number; month: number; day: number } {
  const wall = new Date(at.getTime() + zoneOffset(at.getTime(), zone));
  return {
    year: wall.getUTCFullYear(),
    month: wall.getUTCMonth() + 1,
    day: wall.getUTCDate(),
  };
}

/**
 * Today's calendar date in `zone`, as YYYY-MM-DD: what a front desk in that
 * zone calls "today", whatever the browser's clock says. An unknown zone
 * falls back to the browser's.
 */
export function todayInZone(zone: string, now: Date = new Date()): string {
  const { year, month, day } = zonedDate(
    now,
    isValidTimeZone(zone) ? zone : browserTimeZone(),
  );
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** Whole calendar days from `from` to `to`, counted in `zone`. */
export function calendarDaysBetween(
  from: Date,
  to: Date,
  zone: string,
): number {
  const dayNumber = (at: Date) => {
    const { year, month, day } = zonedDate(at, zone);
    return Date.UTC(year, month - 1, day) / (24 * 60 * 60 * 1000);
  };
  return dayNumber(to) - dayNumber(from);
}

/** Short zone name at that instant, e.g. "EDT" or "GMT+2". */
export function zoneAbbreviation(zone: string, at: Date = new Date()): string {
  return (
    new Intl.DateTimeFormat('en-US', { timeZone: zone, timeZoneName: 'short' })
      .formatToParts(at)
      .find((part) => part.type === 'timeZoneName')?.value ?? zone
  );
}

/** "Nov 14, 6:00 PM" in the zone, plus the zone name when it isn't the browser's. */
export function formatInZone(
  value: string | null | undefined,
  zone: string,
): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const text = new Intl.DateTimeFormat(undefined, {
    timeZone: zone,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  return zone === browserTimeZone()
    ? text
    : `${text} ${zoneAbbreviation(zone, date)}`;
}

/** "Dec 15, 7:35 PM" in the browser zone. */
export function formatShortDateTime(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
}

/** "Dec 15, 2025" in the browser zone — for dates where the time of day is noise. */
export function formatShortDate(value?: string | null): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
}

export type RelativeDay = 'past' | 'yesterday' | 'today' | 'tomorrow' | 'later';

export interface When {
  day: RelativeDay;
  /** "Today", "Tomorrow", "Yesterday" or "Sep 29", in the browser's calendar. */
  dayLabel: string;
  /** "6:30 PM" */
  time: string;
}

/**
 * Splits an instant into its calendar day — named when it is yesterday,
 * today or tomorrow — and the wall-clock time, both in the browser zone (the
 * zone `formatShortDateTime` uses). Null for blanks and unparsable values.
 */
export function describeWhen(
  value?: string | null,
  now: Date = new Date(),
): When | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const dayDiff = Math.round(
    (startOfDay(date) - startOfDay(now)) / (24 * 60 * 60 * 1000),
  );
  const day: RelativeDay =
    dayDiff < -1
      ? 'past'
      : dayDiff === -1
        ? 'yesterday'
        : dayDiff === 0
          ? 'today'
          : dayDiff === 1
            ? 'tomorrow'
            : 'later';
  const named: Partial<Record<RelativeDay, string>> = {
    yesterday: 'Yesterday',
    today: 'Today',
    tomorrow: 'Tomorrow',
  };
  const dayLabel =
    named[day] ??
    new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
    }).format(date);
  const time = new Intl.DateTimeFormat(undefined, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(date);
  return { day, dayLabel, time };
}

/**
 * The calendar day a stored value falls on *for the reader*, as YYYY-MM-DD.
 *
 * Accepts either a plain `YYYY-MM-DD` or a full instant. Slicing an instant
 * gives its UTC day, which across the eastern half of the world is the day
 * before the one the user picked — so an instant is read through the local
 * calendar instead.
 */
export function localDay(value?: string | null): string | null {
  if (!value) return null;
  if (!/[T ]/.test(value)) return value.slice(0, 10);
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** "just now", "5m ago", "3h ago", "yesterday", "4d ago", then "Nov 14". */
export function relativeTime(value?: string | null, now = new Date()): string {
  if (!value) return '';
  const t = new Date(value).getTime();
  if (Number.isNaN(t)) return value;
  const min = Math.round((now.getTime() - t) / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day === 1) return 'yesterday';
  if (day < 7) return `${day}d ago`;
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(t));
}
